import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function fetchMembers() {
  const res = await fetch(`${API}/family/members`, { credentials: "include" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to load members");
  }
  return res.json();
}

async function addMember({ name, phone }) {
  const res = await fetch(`${API}/family/members`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, phone }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Could not add member");
  return data;
}

async function removeMember(phone) {
  const res = await fetch(`${API}/family/members?phone=${encodeURIComponent(phone)}`, {
    method: "DELETE",
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Could not remove member");
  return data;
}

export default function FamilyMembersCard() {
  const [state, setState] = useState({ members: [], max: 4, count: 0, slotsLeft: 4 });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchMembers();
      setState(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onAdd = async (e) => {
    e?.preventDefault();
    if (!phoneInput.trim()) {
      toast.error("Enter a phone number");
      return;
    }
    setAdding(true);
    try {
      const data = await addMember({ name: nameInput.trim() || "Member", phone: phoneInput.trim() });
      setState(data);
      setNameInput("");
      setPhoneInput("");
      setShowForm(false);
      toast.success(`${nameInput || "Member"} added to your family plan.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };

  const onRemove = async (phone) => {
    try {
      const data = await removeMember(phone);
      setState(data);
      toast.success("Member removed.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="cartiq-card" data-testid="family-members-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#1D9E75] font-bold mb-1">
            — Family plan
          </div>
          <h2 className="font-display uppercase text-2xl flex items-center gap-2">
            <Users className="w-5 h-5 text-[#1D9E75]" /> Members
          </h2>
          <p className="text-xs text-[#888] mt-2">
            Up to {state.max} household members share your Family dashboard, budget and WhatsApp insights.
          </p>
        </div>
        <div
          className="text-[11px] uppercase tracking-[0.18em] text-[#1D9E75] font-bold font-mono-num"
          data-testid="family-count"
        >
          {state.count}/{state.max}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#888]">Loading members…</div>
      ) : (
        <>
          <ul className="space-y-2 mb-4" data-testid="family-members-list">
            {state.members.length === 0 && (
              <li className="text-sm text-[#888] italic" data-testid="family-empty">
                No members yet. Add up to {state.max} phone numbers below.
              </li>
            )}
            {state.members.map((m) => (
              <li
                key={m.phone}
                className="flex items-center justify-between border border-[#2A2A35] bg-[#0F0F14] px-3 py-2.5"
                data-testid={`family-member-${m.phone}`}
              >
                <div>
                  <div className="text-sm text-[#F0EDE8] font-bold">{m.name}</div>
                  <div className="text-xs text-[#888] font-mono-num">{m.phone}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(m.phone)}
                  className="text-[#888] hover:text-[#E24B4A] p-1"
                  aria-label={`Remove ${m.name}`}
                  data-testid={`family-remove-${m.phone}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>

          {state.slotsLeft > 0 && !showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="w-full border border-[#1D9E75] text-[#1D9E75] hover:bg-[#1D9E75] hover:text-white font-bold uppercase tracking-wider py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
              data-testid="family-add-btn"
            >
              <Plus className="w-4 h-4" /> Add a household member
            </button>
          )}

          {showForm && (
            <form onSubmit={onAdd} className="space-y-2" data-testid="family-add-form">
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Name"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  className="cartiq-input text-sm"
                  data-testid="family-name-input"
                />
                <input
                  type="tel"
                  placeholder="+91 98xxxxxxxx"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  className="cartiq-input text-sm font-mono-num"
                  required
                  data-testid="family-phone-input"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={adding}
                  className="flex-1 bg-[#1D9E75] text-white font-bold uppercase tracking-wider py-2.5 text-sm disabled:opacity-60"
                  data-testid="family-save-btn"
                >
                  {adding ? "Adding…" : "Add member"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setNameInput(""); setPhoneInput(""); }}
                  className="px-4 border border-[#2A2A35] text-[#888] hover:text-[#F0EDE8] font-bold uppercase tracking-wider py-2.5 text-sm"
                  data-testid="family-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {state.slotsLeft === 0 && (
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#888] text-center mt-2" data-testid="family-full">
              Family plan limit reached · {state.max}/{state.max} members connected
            </div>
          )}
        </>
      )}
    </div>
  );
}
